// Read translated strings injected by the Liquid template
const i18n = JSON.parse(document.getElementById('drops-calc-i18n')?.textContent || '{}');

// Tracks the element that triggered the lead modal so focus can be restored on close
let _modalTrigger = null;

// Lazy-load pdfmake + vfs_fonts only when a report is requested
let _pdfMakeLoaded = false;
let _pdfMakeLoadPromise = null;

function loadPdfMake() {
  if (_pdfMakeLoaded) return Promise.resolve();
  if (_pdfMakeLoadPromise) return _pdfMakeLoadPromise;
  _pdfMakeLoadPromise = new Promise((resolve, reject) => {
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/pdfmake.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/vfs_fonts.js';
      s2.onload = () => { _pdfMakeLoaded = true; resolve(); };
      s2.onerror = (err) => { _pdfMakeLoadPromise = null; reject(err); };
      document.head.appendChild(s2);
    };
    s1.onerror = (err) => { _pdfMakeLoadPromise = null; reject(err); };
    document.head.appendChild(s1);
  });
  return _pdfMakeLoadPromise;
}

// --- Analytics helper ---
// Uses GA4 gtag() if available, otherwise pushes to dataLayer (for GTM)
function trackEvent(eventName, params = {}) {
  try {
    // 1) Send to GA4 if gtag is present
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }

    // 2) ALSO push a GTM-style object into dataLayer if it exists
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: eventName,
        ...params,
      });
    }

    // If neither gtag nor dataLayer exists, we just do nothing
  } catch (e) {
    // Never break the calculator because analytics failed
  }
}

const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzam7hREEWBKLBDp47doPj-KzZQL1moLZs5YdQG9I4e0kc3vUOk3hyywgXfQD3NClByYQ/exec";

function onUnitToggle() {
  const toggle = document.getElementById("unitToggle");
  const isImperial = toggle.checked;

  // Save preference locally (per browser/device)
  try {
    localStorage.setItem("drops_calc_unit", isImperial ? "imperial" : "metric");
  } catch (e) {
    // Fail silently if localStorage is unavailable
  }

  // 🔹 Log the toggle to GA4
  trackEvent("drop_calc_unit_toggle", {
    unit: isImperial ? "imperial" : "metric"
  });

  // Update labels
  document.getElementById("heightLabel").textContent = isImperial
    ? (i18n.unit_height_ft || "Height (feet):")
    : (i18n.unit_height_m  || "Height (metres):");
  document.getElementById("massLabel").textContent = isImperial
    ? (i18n.unit_mass_lb || "Mass (lb):")
    : (i18n.unit_mass_kg || "Mass (kg):");

  // Highlight selected unit label
  const labelMetric = document.getElementById("labelMetric");
  const labelImperial = document.getElementById("labelImperial");

  if (isImperial) {
    labelImperial.classList.add("selected-unit");
    labelMetric.classList.remove("selected-unit");
  } else {
    labelMetric.classList.add("selected-unit");
    labelImperial.classList.remove("selected-unit");
  }

  // Immediately re-run calculation
  calculate();
}


const whiteBackgroundPlugin = {
  id: "whiteBackground",
  beforeDraw: (chart) => {
    const ctx = chart.canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = "white"; // or use your preferred background color
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// --- Scenario configuration ---

const SCENARIO_GRID = {
  veryLight: { // < 1 kg
    veryLow: "A small part or very light tool dropped from hand or bench height, unlikely to result in serious injury. Ensure that correct PPE is still worn.",
    low:   "A light item (e.g. handheld radio, water bottle, multimeter or small hand tool) dropped from a step ladder or low platform above head height.",
    medium:"A light item carried on the body (e.g. radio, water bottle, multimeter or small hand tool) dropped from a scaffold deck or mezzanine over a walkway.",
    high:  "A light item such as a handheld radio, water bottle or multimeter dropped from a high platform or crane walkway over a work area."
  },
  light: { // ~1–6.7 kg
    veryLow: "A small hand tool or loose fitting (e.g. spanner, nut/bolt) dropped from hand or bench height onto feet or lower legs in a workshop or deck area.",
    low:   "A small hand tool or loose fitting (e.g. spanner, nut/bolt) or small piece of equipment dropped from a step ladder or low platform above head height.",
    medium:"A hand tool or small item of equipment (e.g. compact light fitting or junction box) dropped from a two-storey scaffold or pipe rack over a walkway.",
    high:  "A small tool or small piece of equipment (such as a light, junction box or speaker) dropped from a high platform or crane walkway above a work area."
  },
  medium: { // >~6.7–20 kg
    veryLow: "A heavier hand tool or small piece of equipment (e.g. impact wrench, small valve or junction box) dropped from hand or bench height onto feet or lower legs.",
    low:   "A heavier hand tool, tool bag or compact item of equipment (e.g. small valve or junction box) dropped from a short ladder or first-level platform onto someone working below.",
    medium:"A power tool or compact overhead fitting (e.g. floodlight, junction box) dropped from a scaffold deck or mezzanine over a work area.",
    high:  "A tool bag, heavy tool, or a small overhead fitting (e.g. floodlight or junction box) falling from a high platform or structure onto personnel or equipment below."
  },
  heavy: { // >20–50 kg
    veryLow: "A heavy component such as a valve, junction box or equipment housing dropped while being handled at waist height, with potential for serious crush injuries to feet or lower limbs.",
    low:   "A valve, junction box, or equipment housing dropped from a low platform or over-head beam onto someone beneath.",
    medium:"A floodlight, junction box, or bracket assembly falling from a platform or pipe rack over a walkway or work area.",
    high:  "A larger equipment component (e.g. light fitting, section of pipe, bracket) falling from a high structure or topside platform."
  }
};

const SCENARIO_FALLBACKS = {
  veryLight: "A small part or very light tool dropped from hand or bench height, unlikely to result in serious injury. Ensure that correct PPE is still worn.",
  veryHeavyOrHigh: "An object falling with significant force, representing a serious dropped object hazard that should be assessed in detail.",
  generic: "A dropped object scenario that should be assessed in detail considering people, equipment and work area below."
};

function getWeightBand(weightKg) {
  if (weightKg < 1) return "veryLight";
  if (weightKg <= 6.7) return "light";
  if (weightKg <= 20) return "medium";
  if (weightKg <= 50) return "heavy";
  return "veryHeavyOrHigh";
}

function getHeightBand(heightM) {
  if (heightM < 1) return "veryLow";
  if (heightM <= 3) return "low";
  if (heightM <= 7) return "medium";
  if (heightM <= 15) return "high";
  return "veryHigh";
}

function getScenarioText(weightKg, heightM) {
  const weightBand = getWeightBand(weightKg);
  const heightBand = getHeightBand(heightM);

  // 1) Prefer explicit grid mappings when they exist
  if (SCENARIO_GRID[weightBand] && SCENARIO_GRID[weightBand][heightBand]) {
    return SCENARIO_GRID[weightBand][heightBand];
  }

  // 2) Fallbacks for edge cases

  // Very light object from hand/bench height (<1 m)
  if (weightBand === "veryLight" && heightBand === "veryLow") {
    return SCENARIO_FALLBACKS.veryLight;
  }

  // Very high drops or very heavy/large loads
  if (weightBand === "veryHeavyOrHigh" || heightBand === "veryHigh") {
    return SCENARIO_FALLBACKS.veryHeavyOrHigh;
  }

  // Generic catch-all
  return SCENARIO_FALLBACKS.generic;
}


// --- Product recommendation configuration ---

// 1) Define product "families"
const PRODUCT_FAMILIES = {
  toolTether: {
    title: "Tool Tethers",
    description:
      "Prevent hand tools such as hammers and spanners from becoming dropped objects when working at height.",
    image: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/dropsafe-tool-tethering-773.jpg?v=1767685855",
    ctaLabel: "Browse tool tethers",
    ctaUrl: "/collections/dropsafe-tool-tethering"
  },
  nets: {
    title: "Nets",
    description:
      "Capture and secure overhead equipment such as lights, junction boxes and cameras to prevent dropped objects.",
    image: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/dropsafe-nets-225.jpg?v=1767686540",
    ctaLabel: "Browse nets",
    ctaUrl: "/collections/dropsafe-nets"
  },
  barrier: {
    title: "Barrier",
    description:
      "Prevent tools and equipment from falling through gaps in guardrailing.",
    image: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/Dropsafe_Net_Video_24_Frames_Compilation_Clean.00_00_05_17.Still002.jpg?v=1753952348",
    ctaLabel: "Enquire about the Barrier",
    ctaUrl: "/pages/request-quote-barrier"
  },
  pouches: {
    title: "Pouches",
    description:
      "Prevent radios, water bottles and other smaller items from becoming dropped objects.",
    image: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/home-pouch-wm.png?v=1736491361",
    ctaLabel: "Browse pouches",
    ctaUrl: "/collections/dropsafe-pouch"
  },
  custom: {
    title: "Custom products",
    description:
      "We design custom products for specific scenarios where our standard range is unsuitable. Get in touch to hear more.",
    image: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/custom_product_image.png?v=1764313105",
    ctaLabel: "Get in touch",
    ctaUrl: "/pages/contact"
  },
};

// 2) Your grid: weight band + height band -> which families to show
const PRODUCT_MATRIX = {
  veryLight: {
    low:   ["toolTether", "pouches"],
    medium:["toolTether", "pouches"],
    high:  ["toolTether", "pouches"],
    veryHigh: ["pouches", "toolTether", "barrier"]
  },
  light: {
    low:   ["toolTether", "barrier"],
    medium:["toolTether", "nets", "barrier"],
    high:  ["toolTether", "nets", "barrier"],
    veryHigh: ["toolTether", "nets", "barrier"]
  },
  medium: {
    low:   ["nets", "barrier"],
    medium:["nets", "barrier"],
    high:  ["nets", "barrier"],
    veryHigh: ["nets", "barrier"]
  },
  heavy: {
    low:   ["nets", "barrier"],
    medium:["nets", "barrier"],
    high:  ["nets", "barrier"],
    veryHigh: ["nets", "barrier"]
  },
  veryHeavyOrHigh: {
    low:   ["custom"],
    medium:["custom"],
    high:  ["custom"],
    veryHigh: ["custom"]
  }
  // Other cases will just not show products
};

// 3) Look up product families based on the actual mass & height
function getProductFamiliesForScenario(weightKg, heightM) {
  const weightBand = getWeightBand(weightKg);
  const heightBand = getHeightBand(heightM);

  const matrixRow = PRODUCT_MATRIX[weightBand] || {};
  const familyKeys = matrixRow[heightBand] || [];

  // De-duplicate and limit to max 3
  const uniqueKeys = Array.from(new Set(familyKeys)).slice(0, 3);

  return uniqueKeys
  .map((key) => {
    const def = PRODUCT_FAMILIES[key];
    if (!def) return null;
    return {
      familyKey: key,
      ...def,
    };
  })
  .filter(Boolean);
}

// 4) Render product cards into the HTML container
function renderProductRecommendations(weightKg, heightM, severity) {
  const container = document.getElementById("drops-product-recommendations");
  const grid = document.getElementById("drops-product-grid");

  if (!container || !grid) return;

  const products = getProductFamiliesForScenario(weightKg, heightM);

  // If you ever want to suppress products for very low risk, you could do:
  // if (severity === "SLIGHT") { container.style.display = "none"; grid.innerHTML = ""; return; }

  if (!products.length) {
    container.style.display = "none";
    grid.innerHTML = "";
    return;
  }

  const cardsHtml = products
  .map(
    (p) => `
      <article class="drops-product-card">
        <div class="drops-product-image-wrapper">
          <img src="${p.image}" alt="${p.title}" loading="lazy">
        </div>
        <div class="drops-product-body">
          <h4>${p.title}</h4>
          <p>${p.description}</p>
          <a href="${p.ctaUrl}"
            class="drops-product-cta"
            data-family="${p.familyKey}"
            target="_blank"
            rel="noopener noreferrer">
            ${p.ctaLabel}
          </a>
        </div>
      </article>
    `
  )
  .join("");

  // Inject cards
  grid.innerHTML = cardsHtml;
  container.style.display = "block";

  // Attach tracking to each CTA – handle left + middle click
  const links = grid.querySelectorAll(".drops-product-cta");
  links.forEach((link) => {
    // avoid double-binding if we re-render
    if (link.dataset.dropsClickBound === "1") return;

    link.addEventListener("mousedown", function (event) {
      // 0 = left, 1 = middle. Ignore right-click/context menu.
      if (event.button !== 0 && event.button !== 1) return;

      const familyKey = this.getAttribute("data-family") || "unknown";

      trackEvent("drop_calc_product_click", {
        product_family: familyKey,
      });
    });

    link.dataset.dropsClickBound = "1";
  });
}


function drawChart(userMass, userHeight) {
  // ----- 1. Dynamic domain (in SI units) -----
  const baseXMin = 1;
  const baseXMax = 10;
  const baseYMin = 0;
  const baseYMax = 15;

  const point = { x: userMass, y: userHeight };

  // Padding around the point (15% or 0.5, whichever is larger)
  const padX = Math.max(0.5, point.x * 0.15);
  const padY = Math.max(0.5, point.y * 0.15);

  let xMin = baseXMin;
  let xMax = baseXMax;
  let yMin = baseYMin;
  let yMax = baseYMax;

  // Allow domain to expand to include the point + padding
  if (point.x - padX < xMin) {
    xMin = Math.max(0.1, Math.floor((point.x - padX) * 10) / 10);
  }
  if (point.x + padX > xMax) {
    xMax = Math.ceil((point.x + padX) * 10) / 10;
  }
  if (point.y + padY > yMax) {
    yMax = Math.ceil((point.y + padY) * 10) / 10;
  }

  // ----- 2. Risk boundary functions (same as app) -----
  function slightCurve(m) {
    return 4.5633 * Math.pow(m, -0.8489);
  }
  function minorCurve(m) {
    return 6.6126 * Math.pow(m, -0.8472);
  }
  function majorCurve(m) {
    return 10.5509 * Math.pow(m, -0.8476);
  }

  function makeCurveData(fn, numPoints = 100) {
    const data = [];
    const step = (xMax - xMin) / (numPoints - 1);
    for (let i = 0; i < numPoints; i++) {
      const x = xMin + i * step;
      const y = fn(x);
      data.push({ x, y });
    }
    return data;
  }

  const slightData = makeCurveData(slightCurve);
  const minorData = makeCurveData(minorCurve);
  const majorData = makeCurveData(majorCurve);
  const fatalY = yMax; // top of the chart becomes the red band top
  const fatalData = makeCurveData(() => fatalY);

  // ----- 3. Build Chart.js datasets using x/y points -----
  const data = {
    datasets: [
      {
        label: "Slight",
        data: slightData,
        backgroundColor: "#59AC40",
        borderColor: "#59AC40",
        fill: "origin",
        order: 1,
        pointRadius: 0,
        borderWidth: 0,
        parsing: false,
      },
      {
        label: "Minor",
        data: minorData,
        backgroundColor: "#FBD500",
        borderColor: "#FBD500",
        fill: "-1", // fill to previous dataset (Slight)
        order: 2,
        pointRadius: 0,
        borderWidth: 0,
        parsing: false,
      },
      {
        label: "Major",
        data: majorData,
        backgroundColor: "#F36F21",
        borderColor: "#F36F21",
        fill: "-1", // fill to previous dataset (Minor)
        order: 3,
        pointRadius: 0,
        borderWidth: 0,
        parsing: false,
      },
      {
        label: "Fatality",
        data: fatalData,
        backgroundColor: "#D81921",
        borderColor: "#D81921",
        fill: "-1", // fill to previous dataset (Major)
        order: 4,
        pointRadius: 0,
        borderWidth: 0,
        parsing: false,
      },
      {
        label: "Incident",
        data: [{ x: userMass, y: userHeight }],
        type: "scatter",
        showLine: false,
        pointBackgroundColor: "black",
        pointBorderColor: "white",
        pointRadius: 6,
        parsing: false,
      },
    ],
  };

  // ----- 4. Chart config with dynamic axes (kg / lb, m / ft) -----
  const KG_TO_LB = 2.20462;
  const M_TO_FT = 3.28084;

  const config = {
    type: "line",
    data: data,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "linear",
          position: "bottom",
          title: {
            display: true,
            text: "Mass (kg)",
            color: "white",
          },
          ticks: {
            color: "white",
          },
          min: xMin,
          max: xMax,
        },
        x2: {
          type: "linear",
          position: "top",
          min: xMin * KG_TO_LB,
          max: xMax * KG_TO_LB,
          title: {
            display: true,
            text: "Mass (lb)",
            color: "white",
          },
          ticks: {
            color: "white",
            stepSize: 2,
          },
          grid: {
            drawOnChartArea: false,
          },
        },
        y: {
          type: "linear",
          position: "left",
          title: {
            display: true,
            text: "Height (m)",
            color: "white",
          },
          ticks: {
            color: "white",
          },
          min: yMin,
          max: yMax,
        },
        y2: {
          type: "linear",
          position: "right",
          min: yMin * M_TO_FT,
          max: yMax * M_TO_FT,
          title: {
            display: true,
            text: "Height (ft)",
            color: "white",
          },
          ticks: {
            color: "white",
            stepSize: 10,
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: "white",
          },
        },
      },
    },
  };

  // ----- 5. Clear previous chart & render new one -----
  if (window.severityChart && typeof window.severityChart.destroy === "function") {
    window.severityChart.destroy();
  }

  const ctx = document.getElementById("severityChart").getContext("2d");
  window.severityChart = new Chart(ctx, config);
}


function getSeverityFromPowerLaw(mass, height) {
  const useLimited = mass < 1;

  const curves = {
    slight: useLimited ? { A: 4.4007, B: -1.0015 } : { A: 4.5633, B: -0.8489 },
    minor: useLimited ? { A: 6.4569, B: -0.9396 } : { A: 6.6126, B: -0.8472 },
    major: useLimited ? { A: 10.2972, B: -0.9969 } : { A: 10.5509, B: -0.8476 },
  };

  const h_slight = curves.slight.A * Math.pow(mass, curves.slight.B);
  const h_minor = curves.minor.A * Math.pow(mass, curves.minor.B);
  const h_major = curves.major.A * Math.pow(mass, curves.major.B);

  if (height < h_slight) {
    return {
      severity: i18n.severity_slight_label || "SLIGHT",
      color: "#59AC40",
      description: i18n.severity_slight_desc ||
        "A First Aid Case (FAC). The incident results in little to no injury. Any required treatment is minimal and typically limited to basic first aid.",
    };
  } else if (height < h_minor) {
    return {
      severity: i18n.severity_minor_label || "MINOR",
      color: "#FBD500",
      description: i18n.severity_minor_desc ||
        "A Medical Treatment Case (MTC). The injury requires medical attention beyond first aid but does not lead to time away from work, restricted duties, or job transfer.",
    };
  } else if (height < h_major) {
    return {
      severity: i18n.severity_major_label || "MAJOR",
      color: "#F36F21",
      description: i18n.severity_major_desc ||
        "A Lost Time Incident (LTI). A serious injury that prevents the person from working for a period of time. Also known as a Day Away From Work Case (DAFWC).",
    };
  } else {
    return {
      severity: i18n.severity_fatality_label || "FATALITY",
      color: "#D81921",
      description: i18n.severity_fatality_desc ||
        "A fatal incident in which the injury leads to loss of life.",
    };
  }
}

function copyShareLink(btn) {
  trackEvent("drop_calc_share_link");
  navigator.clipboard.writeText(window.location.href).then(() => {
    const originalText = btn.textContent;
    btn.textContent = i18n.copied || "Copied!";
    setTimeout(() => {
      btn.textContent = originalText;
    }, 2000);
  });
}

function openLeadModal() {
  trackEvent("drop_calc_generate_report");
  loadPdfMake(); // Pre-load in background while user fills the form
  _modalTrigger = document.activeElement;
  const modal = document.getElementById("leadModalBackdrop");
  if (modal) {
    modal.style.display = "flex";
    // Move focus into the modal so keyboard/screen-reader users know it opened
    const firstInput = modal.querySelector('input');
    firstInput?.focus();
  }
}

function closeLeadModal() {
  const modal = document.getElementById("leadModalBackdrop");
  if (modal) modal.style.display = "none";
  // Return focus to the element that opened the modal
  _modalTrigger?.focus();
  _modalTrigger = null;
}

// Close modal if clicking outside content, or pressing Escape
window.addEventListener('click', function(event) {
  const modal = document.getElementById("leadModalBackdrop");
  if (modal && event.target === modal) {
    closeLeadModal();
  }
});

document.addEventListener('keydown', function(event) {
  if (event.key !== 'Escape') return;
  const modal = document.getElementById("leadModalBackdrop");
  if (modal && modal.style.display !== 'none') {
    closeLeadModal();
    return;
  }
  const pdfWrapper = document.getElementById("pdf-wrapper");
  if (pdfWrapper && pdfWrapper.style.display !== 'none') {
    closePDFPreview();
  }
});

async function handleLeadSubmit(e) {
  e.preventDefault();

  const btn = document.getElementById("submitLeadBtn");
  const originalText = btn.textContent;
  btn.textContent = "Loading Preview...";
  btn.disabled = true;

  try {
    // Ensure pdfmake is ready before proceeding
    await loadPdfMake();

    // 1. Gather Data
    const isImperial = document.getElementById("unitToggle").checked;
    const leadData = {
      firstName: document.getElementById("leadFirstName").value,
      lastName: document.getElementById("leadLastName").value,
      jobTitle: document.getElementById("leadJobTitle").value,
      email: document.getElementById("leadEmail").value,
      mass: document.getElementById("mass").value,
      height: document.getElementById("height").value,
      unit: isImperial ? "Imperial" : "Metric",
      severity: document.querySelector("#results span")?.innerText || "N/A",
      date: new Date().toISOString()
    };

    // 2. Send to Google Sheets (Async)
    if (GOOGLE_SCRIPT_URL && GOOGLE_SCRIPT_URL.startsWith("http")) {
      fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(leadData)
      }).catch(err => console.log("Sheet Error:", err));
    }

    // 3. Show preview
    await showPDFPreview(leadData);

    // 4. Close modal — preview is now visible
    closeLeadModal();
  } catch (err) {
    console.error("Report generation failed:", err);
    alert("Could not load the report generator. Please check your connection and try again.");
  } finally {
    // Always restore the button so the user can retry
    btn.textContent = originalText;
    btn.disabled = false;
  }
}

async function showPDFPreview(data) {
  // Store data globally so finalDownloadPDF can access it
  window.currentLeadData = data;

  // A. Fill Data into the Preview
  const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const today = new Date().toLocaleDateString('en-US', dateOptions);

  document.getElementById("pdfDate").textContent = today;
  document.getElementById("pdfName").textContent = `${data.firstName} ${data.lastName}`;
  document.getElementById("pdfJob").textContent = data.jobTitle || "";
  
  const massUnit = data.unit === 'Metric' ? 'kg' : 'lbs';
  const heightUnit = data.unit === 'Metric' ? 'm' : 'ft';
  document.getElementById("pdfMass").textContent = `${data.mass} ${massUnit}`;
  document.getElementById("pdfHeight").textContent = `${data.height} ${heightUnit}`;
  
  const energyText = document.querySelector("#results p:nth-of-type(3)").innerText.split(":")[1];
  const severityText = document.querySelector("#results p:nth-of-type(4)").innerText.split(":")[1];
  const descText = document.querySelector("#results p:nth-of-type(5)").innerText;

  document.getElementById("pdfEnergy").textContent = energyText;
  document.getElementById("pdfSeverity").textContent = severityText;
  document.getElementById("pdfDescription").textContent = descText;

  // B. Handle Chart Image
  if (window.severityChart) {
    const chart = window.severityChart;
    const originalOptions = JSON.parse(JSON.stringify(chart.options));

    // Force Black Text for Snapshot
    const black = "#000000";
    ['x', 'x2', 'y', 'y2'].forEach(axis => {
      if (chart.options.scales[axis]) {
        if (!chart.options.scales[axis].ticks) chart.options.scales[axis].ticks = {};
        chart.options.scales[axis].ticks.color = black;
        if (chart.options.scales[axis].title) chart.options.scales[axis].title.color = black;
      }
    });
    if (chart.options.plugins && chart.options.plugins.legend) {
        chart.options.plugins.legend.labels.color = black;
    }
    
    chart.update();
    await new Promise(resolve => setTimeout(resolve, 200)); 

    // Set the image source
    document.getElementById("pdfChartImage").src = chart.toBase64Image();

    // Revert Chart to White
    chart.options = originalOptions;
    ['x', 'x2', 'y', 'y2'].forEach(axis => {
        if(chart.options.scales[axis]) {
            if(chart.options.scales[axis].ticks) chart.options.scales[axis].ticks.color = "white";
            if(chart.options.scales[axis].title) chart.options.scales[axis].title.color = "white";
        }
    });
    if (chart.options.plugins && chart.options.plugins.legend) {
        chart.options.plugins.legend.labels.color = "white";
    }
    chart.update();
  }

  // C. Reveal the Preview Overlay
  const wrapper = document.getElementById("pdf-wrapper");
  wrapper.style.display = "flex"; // Shows the grey overlay
  
  // Store the filename for later use
  window.currentPdfName = `Dropsafe_Report_${data.lastName}.pdf`;
}

function closePDFPreview() {
  document.getElementById("pdf-wrapper").style.display = "none";
}

async function finalDownloadPDF() {
  const data = window.currentLeadData;
  if (!data) {
    alert("Report data is missing. Please try calculating again.");
    return;
  }

  const filename = window.currentPdfName || `Dropsafe_Report_${data.lastName || 'User'}.pdf`;
  
  const btn = document.querySelector(".pdf-btn-download");
  const originalText = btn.textContent;
  btn.textContent = "Generating PDF...";
  btn.disabled = true;

  try {
    // 1. Get Images (Logo & Chart)
    // The chart image is already generated in base64 by showPDFPreview and put into the img tag
    const chartImgBase64 = document.getElementById("pdfChartImage").src;
    
    // Get the logo base64
    const logoEl = document.getElementById("pdfLogo");
    const logoBase64 = await getBase64ImageFromElement(logoEl);

    // 2. Load Custom Fonts (HelveticaNeueLT)
    if (!pdfMake.vfs) pdfMake.vfs = {};

    // ⚠️ REPLACE THESE URLs with the actual links to your .ttf files in Shopify Files
    const fontUrls = {
      regular: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/HelveticaNeueLTStd-Md.otf?v=1770005461", 
      bold: "https://cdn.shopify.com/s/files/1/0558/6114/9768/files/HelveticaNeueLTStd-Bd.otf?v=1770005461"
    };

    await Promise.all([
      loadFontToVfs('HelveticaNeueLT-Regular.ttf', fontUrls.regular),
      loadFontToVfs('HelveticaNeueLT-Bold.ttf', fontUrls.bold)
    ]);

    pdfMake.fonts = {
      HelveticaNeueLT: {
        normal: 'HelveticaNeueLT-Regular.ttf',
        bold: 'HelveticaNeueLT-Bold.ttf',
        italics: 'HelveticaNeueLT-Regular.ttf', // Fallback to regular if italic not available
        bolditalics: 'HelveticaNeueLT-Bold.ttf' // Fallback to bold
      },
      Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf'
      }
    };

    // 3. Prepare Text
    const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const today = new Date().toLocaleDateString('en-US', dateOptions);
    
    const massUnit = data.unit === 'Metric' ? 'kg' : 'lbs';
    const heightUnit = data.unit === 'Metric' ? 'm' : 'ft';

    // Grab calculated results from the DOM (as they are formatted there)
    const energyText = document.getElementById("pdfEnergy").textContent;
    const severityText = document.getElementById("pdfSeverity").textContent;
    const descText = document.getElementById("pdfDescription").textContent;

    // Capture current URL for the link
    const reportUrl = window.location.href;

    // 4. Define PDF Document
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 60], // [left, top, right, bottom]
      content: [
        // Header
        {
          columns: [
            { image: logoBase64, width: 140 },
            { text: today, alignment: 'right', color: '#666666', fontSize: 10, margin: [0, 10, 0, 0] }
          ],
          margin: [0, 0, 0, 20]
        },
        
        // Title
        { text: 'Dropped Object Risk Report', fontSize: 22, bold: true, color: '#FBD500', margin: [0, 0, 0, 5] },
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.5, lineColor: '#000000' }], margin: [0, 0, 0, 25] },

        // Prepared For
        {
          text: [
            { text: 'Prepared For: ', bold: true },
            `${data.firstName} ${data.lastName}\n`,
            { text: 'Job Title: ', bold: true },
            `${data.jobTitle || '-'}`
          ],
          fontSize: 11,
          margin: [0, 0, 0, 25]
        },

        // Input Grid
        {
          columns: [
            {
              stack: [
                { text: 'INPUT MASS', fontSize: 9, color: '#666666', bold: true, margin: [0, 0, 0, 2] },
                { text: `${data.mass} ${massUnit}`, fontSize: 16, bold: true }
              ],
              fillColor: '#f9f9f9',
              margin: [0, 0, 10, 0],
              padding: 10 // pdfmake doesn't support padding in columns directly without a table, but we'll keep it simple
            },
            {
              stack: [
                { text: 'INPUT HEIGHT', fontSize: 9, color: '#666666', bold: true, margin: [0, 0, 0, 2] },
                { text: `${data.height} ${heightUnit}`, fontSize: 16, bold: true }
              ],
              fillColor: '#f9f9f9',
              margin: [10, 0, 0, 0]
            }
          ],
          margin: [0, 0, 0, 25]
        },

        // Analysis Results (Yellow Box)
        {
          table: {
            widths: ['*'],
            body: [[
              {
                stack: [
                  { text: 'ANALYSIS RESULTS', fontSize: 10, bold: true, color: '#444', margin: [0, 0, 0, 8] },
                  { text: [{ text: 'Energy at Impact: ', color: '#000' }, { text: energyText, bold: true }], margin: [0, 0, 0, 4] },
                  { text: [{ text: 'Severity Level: ', color: '#000' }, { text: severityText, bold: true }], margin: [0, 0, 0, 4] },
                  { text: descText, fontSize: 10, color: '#333', margin: [0, 4, 0, 0] }
                ],
                fillColor: '#fffbe6',
                borderColor: ['#FBD500', '#eeeeee', '#eeeeee', '#eeeeee'],
                borderWidth: [4, 1, 1, 1],
                margin: [15, 10, 15, 10]
              }
            ]]
          },
          layout: {
            defaultBorder: false
          },
          margin: [0, 0, 0, 25]
        },

        // Chart
        { text: 'Risk Visualization', fontSize: 12, bold: true, margin: [0, 0, 0, 10] },
        { image: chartImgBase64, width: 500, alignment: 'center' }
      ],
      footer: function(currentPage, pageCount) {
        return {
          stack: [
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#eeeeee' }], margin: [0, 0, 0, 10] },
            { text: 'Generated by Dropsafe Drops Calculator | dropsafe.com', alignment: 'center', fontSize: 8, color: '#666' },
            { text: 'View Calculation Online', link: reportUrl, alignment: 'center', fontSize: 8, color: '#444', decoration: 'underline', margin: [0, 2, 0, 0] },
            { text: 'This report is a guide only and does not replace a professional risk assessment.', alignment: 'center', fontSize: 7, color: '#999', margin: [0, 2, 0, 0] }
          ],
          margin: [40, 0, 40, 0]
        };
      },
      defaultStyle: {
        font: 'HelveticaNeueLT',
        fontSize: 10,
        color: '#232326'
      }
    };

    pdfMake.createPdf(docDefinition).download(filename);

  } catch(e) {
    console.error("PDF Generation Error:", e);
    alert("An error occurred while generating the PDF. Please check console for details.");
  } finally {
    btn.textContent = originalText;
    btn.disabled = false;
  }
}

function calculate() {
  const g = 9.81; 
  const isImperial = document.getElementById("unitToggle").checked;
  let height = parseFloat(document.getElementById("height").value);
  let mass = parseFloat(document.getElementById("mass").value);

  // Validation
  if (isNaN(height) || isNaN(mass) || height <= 0 || mass <= 0) {
    document.getElementById("results").innerHTML = `<p>${i18n.error_invalid || 'Please enter valid, positive numbers for height and mass.'}</p>`;
    if (document.getElementById("drops-scenario")) document.getElementById("drops-scenario").style.display = "none";
    if (document.getElementById("drops-product-recommendations")) document.getElementById("drops-product-recommendations").style.display = "none";
    
    // Hide buttons on error
    const shareBtn = document.getElementById("shareResultBtn");
    if (shareBtn) shareBtn.style.display = "none";
    
    const dlBtn = document.getElementById("downloadReportBtn");
    if (dlBtn) dlBtn.style.display = "none";

    document.getElementById("disclaimer").style.display = "none";
    return;
  }

  // Convert for Calculation (Logic uses Metric internally)
  if (isImperial) {
    height = height * 0.3048;   
    mass = mass * 0.453592;     
  }

  const time = Math.sqrt((2 * height) / g);
  const speed = Math.sqrt(2 * g * height); 
  const speedKmh = speed * 3.6;
  const energy = mass * g * height;
  const severityInfo = getSeverityFromPowerLaw(mass, height);
  const severity = severityInfo.severity;
  const color = severityInfo.color;

  const resultHTML = `
    <h2>${i18n.results_heading || 'Results'}</h2>
    <p><strong>${i18n.time_to_impact || 'Time Until Impact'}:</strong> ${time.toFixed(2)} seconds</p>
    <p><strong>${i18n.speed_at_impact || 'Speed at Impact'}:</strong> ${speed.toFixed(2)} m/s (${speedKmh.toFixed(2)} km/h)</p>
    <p><strong>${i18n.energy_at_impact || 'Energy at Impact'}:</strong> ${energy.toFixed(2)} Joules</p>
    <p><strong>${i18n.severity_level || 'Severity Level'}:</strong> <span style="color:${color};">${severity}</span></p>
    <p>${severityInfo.description}</p>
  `;

  document.getElementById("results").innerHTML = resultHTML;


  // Show buttons
  const shareBtn = document.getElementById("shareResultBtn");
  if (shareBtn) shareBtn.style.display = "inline-flex";

  const dlBtn = document.getElementById("downloadReportBtn");
  if (dlBtn) dlBtn.style.display = "inline-flex";

  // Charts & Scenarios
  drawChart(mass, height);

  const scenarioText = getScenarioText(mass, height);
  const scenarioBlock = document.getElementById("drops-scenario");
  const scenarioTextEl = document.getElementById("drops-scenario-text");

  if (scenarioText && scenarioBlock && scenarioTextEl) {
    scenarioTextEl.textContent = scenarioText;
    scenarioBlock.style.display = "block";
  } else if (scenarioBlock) {
    scenarioBlock.style.display = "none";
  }

  renderProductRecommendations(mass, height, severity);

  // URL State
  const url = new URL(window.location);
  url.searchParams.set('mass', document.getElementById('mass').value);
  url.searchParams.set('height', document.getElementById('height').value);
  url.searchParams.set('unit', isImperial ? 'imperial' : 'metric');
  window.history.replaceState({}, '', url);

  // Analytics
  trackEvent("drop_calc_submit", {
    unit: isImperial ? "imperial" : "metric",
    mass_kg: mass,
    height_m: height,
    severity: severity,
    weight_band: getWeightBand(mass),
    height_band: getHeightBand(height)
  });

  document.getElementById("disclaimer").style.display = "block";
}


// Ensure toggle label styling matches default state on load
document.addEventListener("DOMContentLoaded", () => {
  const params = new URLSearchParams(window.location.search);
  const toggle = document.getElementById("unitToggle");

  // If URL has params, use them. They have top priority.
  if (params.has('mass') && params.has('height') && toggle) {
      toggle.checked = (params.get('unit') === 'imperial');
      document.getElementById('mass').value = params.get('mass');
      document.getElementById('height').value = params.get('height');
  }
  // If no URL params, fall back to localStorage for the unit preference.
  else if (toggle) {
    try {
      const savedUnit = localStorage.getItem("drops_calc_unit");
      if (savedUnit === "imperial") {
        toggle.checked = true;
      } else if (savedUnit === "metric") {
        toggle.checked = false;
      }
    } catch (e) {
      // If localStorage fails, just use default from HTML
    }
  }

  onUnitToggle();
});

// Helper to get base64 from an image element
function getBase64ImageFromElement(img) {
  return new Promise((resolve, reject) => {
    if (img.complete && img.naturalHeight !== 0) {
      resolve(imgToBase64(img));
    } else {
      img.onload = () => resolve(imgToBase64(img));
      img.onerror = (e) => { console.warn("Image load failed", e); resolve(null); };
    }
  });
}

function imgToBase64(img) {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/png");
}

async function loadFontToVfs(filename, url) {
  if (!pdfMake.vfs[filename]) {
    try {
      const base64 = await getBase64FromUrl(url);
      if (base64) pdfMake.vfs[filename] = base64;
    } catch (e) {
      console.warn(`Could not load font ${filename} from ${url}`, e);
    }
  }
}

async function getBase64FromUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
